import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { X, Copy, ExternalLink, Check, Loader2, Link2 } from "lucide-react";
import api from "@/lib/api";

const slugifyPreview = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function EmbedShareModal({ manualId, enabled, slug: initialSlug = "", defaultName = "", onSlug, onClose }) {
  const origin = window.location.origin;
  const [slug, setSlug] = useState(initialSlug);
  const [input, setInput] = useState(initialSlug || slugifyPreview(defaultName));
  const [saving, setSaving] = useState(false);
  const idPart = slug || manualId;
  const url = `${origin}/embed/manual/${idPart}`;
  const iframe = `<iframe src="${url}" width="100%" height="600" style="border:0;border-radius:12px" allowfullscreen title="KANN interactieve handleiding"></iframe>`;
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => { QRCode.toDataURL(url, { width: 320, margin: 1, color: { dark: "#232323", light: "#ffffff" } }).then(setQr).catch(() => {}); }, [url]);

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); toast.success("Gekopieerd"); setTimeout(() => setCopied(""), 1500); };

  const saveSlug = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/manuals/${manualId}/slug`, { slug: input || defaultName });
      setSlug(data.slug); setInput(data.slug); onSlug && onSlug(data.slug);
      toast.success("Eigen link opgeslagen");
    } catch (e) { toast.error(e.response?.data?.detail || "Kon link niet opslaan"); }
    finally { setSaving(false); }
  };

  const preview = slugifyPreview(input);
  const dirty = preview !== slug;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="embed-share-modal">
      <div className="bg-kann-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-kann-border/20 sticky top-0 bg-kann-surface">
          <div>
            <div className="text-sm font-semibold text-kann-text-pri">Insluiten op je website</div>
            <div className="text-xs text-kann-text-sec">Deel de interactieve 3D-handleiding met je klanten.</div>
          </div>
          <button onClick={onClose} data-testid="embed-close-btn" className="p-2 rounded-md hover:bg-kann-surface-active text-kann-text-sec"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {!enabled && (
            <div className="text-xs bg-kann-gold/15 text-kann-text-pri rounded-lg px-3 py-2.5" data-testid="embed-not-published">
              Nog niet gepubliceerd. Voeg in de 3D-studio minstens één onderdeel én één stap toe en klik op "Opslaan &amp; publiceren".
            </div>
          )}

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Eigen link (SEO)</label>
            <div className="mt-2 flex items-stretch gap-2">
              <div className="flex-1 flex items-center bg-kann-base border border-kann-border/40 rounded-md overflow-hidden">
                <span className="pl-3 pr-1 py-2 text-xs text-kann-text-sec whitespace-nowrap select-none hidden sm:inline">/embed/manual/</span>
                <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="embed-slug-input" placeholder={slugifyPreview(defaultName) || "eigen-naam"} className="flex-1 min-w-0 bg-transparent px-2 sm:px-1 py-2 text-sm text-kann-text-pri outline-none" />
              </div>
              <button onClick={saveSlug} disabled={saving || !preview} data-testid="embed-slug-save" className="bg-kann-ink text-white font-semibold rounded-md px-4 text-sm hover:bg-black disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Opslaan
              </button>
            </div>
            <p className="text-[11px] text-kann-text-sec mt-1.5">
              Wordt: <span className="font-mono text-kann-text-pri">/embed/manual/{preview || "…"}</span>. Kleine letters en koppeltekens; bij een dubbele naam voegen we automatisch een cijfer toe.
              {slug && <> De oude link met de code blijft ook werken.</>}
            </p>
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Publieke link</label>
            <div className="mt-2 flex gap-2">
              <input readOnly value={url} data-testid="embed-url" className="flex-1 bg-kann-base border border-kann-border/40 rounded-md px-3 py-2 text-sm text-kann-text-pri outline-none" />
              <button onClick={() => copy(url, "url")} data-testid="embed-copy-url" className="border border-kann-border/50 rounded-md px-3 hover:bg-kann-surface-active text-kann-text-sec">{copied === "url" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}</button>
              <a href={url} target="_blank" rel="noreferrer" data-testid="embed-open" className="border border-kann-border/50 rounded-md px-3 flex items-center hover:bg-kann-surface-active text-kann-text-sec"><ExternalLink className="w-4 h-4" /></a>
            </div>
            {dirty && preview && <p className="text-[11px] text-kann-gold mt-1.5" data-testid="embed-slug-unsaved">Klik "Opslaan" hierboven om je eigen link te activeren.</p>}
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Insluitcode (iframe)</label>
            <div className="mt-2 relative">
              <textarea readOnly value={iframe} rows={3} data-testid="embed-iframe" className="w-full bg-kann-base border border-kann-border/40 rounded-md px-3 py-2 text-xs font-mono text-kann-text-pri outline-none resize-none" />
              <button onClick={() => copy(iframe, "iframe")} data-testid="embed-copy-iframe" className="absolute top-2 right-2 border border-kann-border/50 bg-kann-surface rounded-md px-2 py-1 hover:bg-kann-surface-active text-kann-text-sec">{copied === "iframe" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}</button>
            </div>
          </div>

          {qr && (
            <div className="flex items-center gap-4">
              <img src={qr} alt="QR" className="w-24 h-24 rounded-lg border border-kann-border/40" data-testid="embed-qr" />
              <div className="text-xs text-kann-text-sec">Scan de QR-code om de handleiding op een telefoon of tablet te openen — handig naast het product of op verpakking.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
